import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  ThunkAction,
  AnyAction,
} from "@reduxjs/toolkit";
import {
  addAuthorizationBearerToken,
  removeAuthorizationBearerToken,
} from "../../api/punctApi";
import { RootState, Thunk } from "../../redux";
import { UserRole } from "../../generated/graphql";
import { authSerializer } from "./utils/authSerializer";
import { refreshCredentials } from "../auth0/functions/refreshCredentials";

export interface AuthState {
  email?: string;
  userId?: string;
  accessToken?: string;
  companyName?: string;
  name?: string;
  userImage?: string;
  roles?: UserRole[];
  companyId?: number;
  refreshToken?: string;
  expire?: number;
  expiresAt?: number;
}

export interface SuccessfulAuth extends AuthState {
  userId: string;
  accessToken: string;
  refreshToken: string;
}

interface AuthSliceState extends AuthState {
  // False until refreshSavedCredentials (dispatched once from _app.tsx's
  // useEffect) actually resolves - whether it finds a valid session or not.
  // AuthGuard/useAuthGuard must wait for this before deciding whether to
  // redirect: effects fire children-first, so AuthGuard's own effect (a
  // descendant of _app.tsx) always runs before _app.tsx's, and would
  // otherwise conclude "no roles yet" and redirect away before the saved
  // credentials ever get a chance to load - a state update from the
  // dispatch itself doesn't have that problem (it triggers a fresh render
  // for every consumer, regardless of original effect order), only the
  // guard's own redirect-on-mount logic does.
  isInitialized: boolean;
}

// Deliberately NOT authSerializer.load() here: that reads localStorage,
// unavailable server-side, so an already-logged-in user's client-side
// initialState would differ from what the server rendered - every
// component reading auth state (AuthGuard, header nav, HeaderAvatar, ...)
// would then hydrate mismatched under React 18. store.ts's dispatch of
// refreshSavedCredentials() already loads the same saved credentials right
// after the store is created - that's a real async action (resolves on a
// microtask, after the first render), not a hydration-time state read, so
// it updates safely post-mount instead of baking the mismatch in from the
// start.
const initialState: AuthSliceState = { isInitialized: false };

const {
  actions: { resetAuthSuccess, updateAuthSuccess },
  reducer: authReducer,
} = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetAuthSuccess: () => ({ isInitialized: true }),
    updateAuthSuccess: (state, action: PayloadAction<AuthState>) => ({
      ...action.payload,
      isInitialized: true,
    }),
  },
  extraReducers(builder) {
    builder.addCase(refreshSavedCredentials.fulfilled, (state, action) => {
      // action.payload is undefined on the path where the thunk instead
      // delegates to refreshCredentialsAction (which resolves its own
      // credentials via a separate, already-dispatched updateAuthSuccess
      // rather than returning them) - spreading state first, rather than
      // replacing it outright, preserves whatever that nested dispatch
      // already set instead of clobbering it back to just
      // { isInitialized: true }.
      return { ...state, ...action.payload, isInitialized: true };
    });
  },
});

const updateAuth =
  (state: SuccessfulAuth): Thunk<void> =>
  async (dispatch) => {
    try {
      const { accessToken } = state;

      authSerializer.save(state);

      addAuthorizationBearerToken(accessToken);
      dispatch(updateAuthSuccess(state));
    } catch (_err) {
      dispatch(resetAuthSuccess());
    }
  };

const resetAuth = (): Thunk<void> => (dispatch) => {
  authSerializer.reset();
  removeAuthorizationBearerToken();
  dispatch(resetAuthSuccess());
};

// Refresh this long before the access token expires, so a request never
// leaves with a token that runs out on the way.
const EXPIRY_MARGIN_MS = 60_000;
const REFRESH_LOCK_NAME = "punct-auth-refresh";

const isFresh = (auth?: AuthState): boolean =>
  !!auth?.accessToken &&
  !!auth.expiresAt &&
  auth.expiresAt - EXPIRY_MARGIN_MS > Date.now();

// Auth0 answered and refused the refresh token (expired, revoked or reused).
// Anything else - offline, timeout, 429, 5xx - is temporary and must not log
// the user out.
const isRejectedByAuth0 = (err: unknown): boolean => {
  const status = (err as { statusCode?: number } | undefined)?.statusCode;
  return status === 400 || status === 401 || status === 403;
};

// Refresh tokens rotate: each one works once. If two tabs spend the same one,
// Auth0 treats it as theft and revokes the session, so only one tab at a time
// may refresh. Browsers without the Web Locks API skip the lock.
const withCrossTabLock = <T>(fn: () => Promise<T>): Promise<T> =>
  typeof navigator !== "undefined" && navigator.locks
    ? (navigator.locks.request(REFRESH_LOCK_NAME, fn) as Promise<T>)
    : fn();

// The refresh in progress, shared by every caller that needs a token now.
let pendingRefresh: Promise<string | undefined> | undefined;

const refreshCredentialsAction =
  (state: SuccessfulAuth): Thunk<Promise<string | undefined>> =>
  (dispatch) => {
    pendingRefresh ??= withCrossTabLock(async () => {
      // Another tab may have refreshed while we waited for the lock. Its
      // credentials are in storage, and our refresh token is already spent.
      const saved = authSerializer.load();
      if (saved && isFresh(saved)) {
        dispatch(updateAuth(saved));
        return saved.accessToken;
      }

      const newCredentials = await refreshCredentials(
        saved?.refreshToken ?? state.refreshToken,
      );
      if (!newCredentials?.accessToken) {
        dispatch(resetAuth());
        return undefined;
      }
      dispatch(updateAuth(newCredentials));

      // refresh the new credentials again before they expire
      if (newCredentials.expire) {
        setTimeout(
          () => dispatch(refreshCredentialsAction(newCredentials)),
          (newCredentials.expire - 10) * 1000,
        );
      }

      return newCredentials.accessToken;
    })
      .catch((err) => {
        if (isRejectedByAuth0(err)) {
          dispatch(resetAuth());
          return undefined;
        }
        // Keep the session: the current token may still work, and the next
        // request will try to refresh again.
        console.error(err);
        return state.accessToken;
      })
      .finally(() => {
        pendingRefresh = undefined;
      });

    return pendingRefresh;
  };

// Resolves to an access token that is valid for at least another minute,
// refreshing it first when needed. Call this right before sending a request.
const getFreshAccessToken =
  (): Thunk<Promise<string | undefined>> => (dispatch, getState) => {
    const { auth } = getState();
    if (!auth.refreshToken || isFresh(auth)) {
      return Promise.resolve(auth.accessToken);
    }
    return dispatch(refreshCredentialsAction(auth as SuccessfulAuth));
  };

// async thunk that checks the local storage for saved credentials and refreshes them
// this is used in order to not ask for credentials every time the app loads
const refreshSavedCredentials = createAsyncThunk(
  "auth/refreshSavedCredentials",
  async (_, { dispatch }) => {
    try {
      const initialState = authSerializer.load();
      if (!initialState || !initialState.refreshToken) {
        return {};
      }

      // Refresh tokens are single-use/rotating - Auth0 invalidates the old
      // one on every exchange. This thunk runs on every fresh page load
      // (dispatched once in store.ts), so unconditionally refreshing here
      // meant rapid successive page loads (e.g. a test suite's cy.visit()
      // sequence, or just a user clicking around normally) could race to
      // exchange the same soon-to-be-stale token and get "invalid_grant"
      // rejected by Auth0, logging the user out. If the cached access
      // token isn't actually expired yet, there's no need to touch the
      // network at all - just use it, and let refreshCredentialsAction's
      // own setTimeout (still scheduled below) refresh it once it's
      // actually close to expiring.
      if (initialState.expiresAt && initialState.expiresAt > Date.now()) {
        if (initialState.expire) {
          setTimeout(
            () =>
              dispatch(
                refreshCredentialsAction(
                  initialState,
                ) as unknown as ThunkAction<void, unknown, unknown, AnyAction>,
              ),
            initialState.expiresAt - Date.now() - 10_000,
          );
        }
        return initialState;
      }

      // No payload on this path: refreshCredentialsAction already stored the
      // new credentials (see the fulfilled reducer above).
      await dispatch(
        refreshCredentialsAction(initialState) as unknown as ThunkAction<
          Promise<string | undefined>,
          unknown,
          unknown,
          AnyAction
        >,
      );
      return undefined;
    } catch (_err) {
      dispatch(resetAuthSuccess());
    }
  },
);

const getAuthState =
  () =>
  ({ auth }: RootState): AuthState => ({
    userId: auth.userId,
    email: auth.email,
    accessToken: auth.accessToken,
    name: auth.name,
    companyName: auth.companyName,
    userImage: auth.userImage,
    roles: auth.roles,
    companyId: auth.companyId,
    refreshToken: auth.refreshToken,
    expire: auth.expire,
    expiresAt: auth.expiresAt,
  });

const getAccessToken = ({ auth }: RootState): string | undefined =>
  auth.accessToken;

const getUserRoles = ({ auth }: RootState): UserRole[] => auth.roles || [];
const getCompanyId = ({ auth }: RootState) => auth.companyId;
const getIsAuthInitialized = ({ auth }: RootState): boolean =>
  (auth as AuthSliceState).isInitialized;

export {
  authReducer,
  getAccessToken,
  getAuthState,
  getUserRoles,
  getCompanyId,
  getIsAuthInitialized,
  updateAuth,
  resetAuth,
  refreshCredentialsAction,
  refreshSavedCredentials,
  getFreshAccessToken,
};
